import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClientProvider } from "react-query";
import "./styles/instant-theme.css";

import {enUS, ptBR, esES} from "@material-ui/core/locale";
import { SocketContext, SocketManager } from './context/Socket/SocketContext';
import pushNotifications from "./services/pushNotifications";
import { getStoredAuthToken } from "./utils/authTokenStorage";
import queryClient from "./queries/queryClient";

import Routes from "./routes";
import { DraftMessagesProvider } from "./context/Drafts/DraftMessagesContext";
import OrientationLockOverlay from "./components/OrientationLockOverlay";
import { getRequiredOrientation, isProbablyIpad, isTouchDevice } from "./utils/orientationLock";
import { installKanbanDraggingCursorCleanup } from "./pages/Kanban/kanbanDragCursor";

const PUSH_FOREGROUND_HEARTBEAT_MS = 15000;

const App = () => {
    const [locale, setLocale] = useState();

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale = i18nlocale?.substring(0, 2) ?? 'pt';

        if (browserLocale === "pt"){
            setLocale(ptBR);
        }else if( browserLocale === "en" ) {
            setLocale(enUS)
        }else if( browserLocale === "es" )
            setLocale(esES)

    }, []);

    useEffect(() => {
        if (typeof document === "undefined") {
            return undefined;
        }

        return installKanbanDraggingCursorCleanup(document, window);
    }, []);

    useEffect(() => {
        if (!("serviceWorker" in navigator)) {
            return undefined;
        }

        const handleServiceWorkerMessage = (event) => {
            const data = event?.data || {};

            if (data.type === "PUSH_FOREGROUND") {
                if (data.ackId) {
                    try {
                        if (navigator.serviceWorker?.controller) {
                            navigator.serviceWorker.controller.postMessage({
                                type: "PUSH_FOREGROUND_ACK",
                                ackId: data.ackId
                            });
                        } else if (event.source && typeof event.source.postMessage === "function") {
                            event.source.postMessage({
                                type: "PUSH_FOREGROUND_ACK",
                                ackId: data.ackId
                            });
                        }
                    } catch (error) {
                        console.warn("Falha ao enviar ACK de push foreground:", error);
                    }
                }

                if (getStoredAuthToken()) {
                    window.dispatchEvent(
                        new CustomEvent("neozapp:push-foreground", {
                            detail: data
                        })
                    );
                }
                return;
            }

            if (data.type === "NAVIGATE_TO" && typeof data.href === "string") {
                window.dispatchEvent(
                    new CustomEvent("neozapp:push-navigate", {
                        detail: data
                    })
                );
                if (window.location.href !== data.href) {
                    window.location.assign(data.href);
                }
            }
        };

        navigator.serviceWorker.addEventListener("message", handleServiceWorkerMessage);

        return () => {
            navigator.serviceWorker.removeEventListener("message", handleServiceWorkerMessage);
        };
    }, []);

    useEffect(() => {
        if (!pushNotifications.isSupported()) {
            return undefined;
        }

        const ensurePushHealth = async (source) => {
            if (!getStoredAuthToken()) {
                return;
            }

            try {
                const result = await pushNotifications.ensureSubscriptionHealthSafe();
                if (result?.ok && result?.healed) {
                    console.info(`[push-health] subscription healed via ${source}`);
                }
            } catch (_error) {
                // best-effort background heal
            }
        };

        ensurePushHealth("bootstrap");

        const onVisibilityChange = () => {
            if (document.visibilityState === "visible") {
                ensurePushHealth("visibilitychange");
            }
        };

        const onOnline = () => ensurePushHealth("online");
        const onAuthRestored = () => ensurePushHealth("auth-restored");
        const onPageShow = () => ensurePushHealth("pageshow");
        window.addEventListener("online", onOnline);
        window.addEventListener("pageshow", onPageShow);
        window.addEventListener("neozapp:auth-restored", onAuthRestored);
        document.addEventListener("visibilitychange", onVisibilityChange);

        return () => {
            document.removeEventListener("visibilitychange", onVisibilityChange);
            window.removeEventListener("online", onOnline);
            window.removeEventListener("pageshow", onPageShow);
            window.removeEventListener("neozapp:auth-restored", onAuthRestored);
        };
    }, []);

    useEffect(() => {
        if (!pushNotifications.isSupported()) {
            return undefined;
        }

        let heartbeatTimer = null;
        let cancelled = false;

        const clearHeartbeat = () => {
            if (heartbeatTimer) {
                window.clearInterval(heartbeatTimer);
                heartbeatTimer = null;
            }
        };

        const emitPushPresence = async (state) => {
            if (cancelled || !getStoredAuthToken()) {
                return;
            }

            try {
                const endpoint = await pushNotifications.getCurrentEndpoint();
                if (!endpoint || cancelled) {
                    return;
                }

                const companyId = localStorage.getItem("companyId");
                if (!companyId) {
                    return;
                }

                const socket = SocketManager.getSocket(companyId);
                socket.emit("pushPresence:update", {
                    endpoint,
                    state
                });
            } catch (_error) {
                // best-effort presence reporting
            }
        };

        const startHeartbeat = () => {
            if (heartbeatTimer) {
                return;
            }

            heartbeatTimer = window.setInterval(() => {
                emitPushPresence("foreground");
            }, PUSH_FOREGROUND_HEARTBEAT_MS);
        };

        const syncPushPresence = () => {
            if (document.visibilityState === "visible") {
                emitPushPresence("foreground");
                startHeartbeat();
                return;
            }

            clearHeartbeat();
            emitPushPresence("background");
        };

        const onVisibilityChange = () => {
            syncPushPresence();
        };

        const onPageShow = () => {
            syncPushPresence();
        };

        const onPageHide = () => {
            clearHeartbeat();
            emitPushPresence("background");
        };

        const onBeforeUnload = () => {
            clearHeartbeat();
            emitPushPresence("background");
        };

        const onFreeze = () => {
            clearHeartbeat();
            emitPushPresence("background");
        };

        syncPushPresence();

        document.addEventListener("visibilitychange", onVisibilityChange);
        document.addEventListener("freeze", onFreeze);
        window.addEventListener("pageshow", onPageShow);
        window.addEventListener("pagehide", onPageHide);
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            clearHeartbeat();
            emitPushPresence("background");
            cancelled = true;
            document.removeEventListener("visibilitychange", onVisibilityChange);
            document.removeEventListener("freeze", onFreeze);
            window.removeEventListener("pageshow", onPageShow);
            window.removeEventListener("pagehide", onPageHide);
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, []);

    useEffect(() => {
        if (typeof window === "undefined" || !isTouchDevice(window)) {
            return undefined;
        }

        const tryLockOrientation = () => {
            if (isProbablyIpad(window)) {
                try {
                    if (typeof window.screen?.orientation?.unlock === "function") {
                        window.screen.orientation.unlock();
                    }
                } catch (_) { /* Not supported in every browser. */ }
                return;
            }
            const requiredOrientation = getRequiredOrientation(window);
            if (!requiredOrientation || !window.screen?.orientation?.lock) {
                return;
            }

            window.screen.orientation.lock(requiredOrientation).catch(() => {
                // Fallback visual fica a cargo do OrientationLockOverlay
            });
        };

        tryLockOrientation();

        window.addEventListener("pageshow", tryLockOrientation);
        window.addEventListener("orientationchange", tryLockOrientation);
        if (
            window.screen &&
            window.screen.orientation &&
            typeof window.screen.orientation.addEventListener === "function"
        ) {
            window.screen.orientation.addEventListener("change", tryLockOrientation);
        }

        return () => {
            window.removeEventListener("pageshow", tryLockOrientation);
            window.removeEventListener("orientationchange", tryLockOrientation);
            if (
                window.screen &&
                window.screen.orientation &&
                typeof window.screen.orientation.removeEventListener === "function"
            ) {
                window.screen.orientation.removeEventListener("change", tryLockOrientation);
            }
        };
    }, []);

    return (
        <>
          <QueryClientProvider client={queryClient}>
            <SocketContext.Provider value={SocketManager}>
              <DraftMessagesProvider>
                <Routes />
              </DraftMessagesProvider>
            </SocketContext.Provider>
          </QueryClientProvider>
          <OrientationLockOverlay />
        </>
    );
};

export default App;
